import {
  type InfiniteData,
  useInfiniteQuery,
  type UseInfiniteQueryOptions
} from '@tanstack/react-query'
import { Divider, Empty, Flex, Select, Spin } from 'antd'
import type { AnyObject } from 'antd/es/_util/type'
import type { DefaultOptionType, SelectProps } from 'antd/es/select'
import { AxiosError } from 'axios'
import {
  type ReactElement,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

import type { QueryKey } from 'src/api/main'
import type { Options, TDataShape } from 'src/api/main/client'
import { useFilterLabels } from 'src/context'
import { useDebouncedValue } from 'src/hooks'
import { type OptionTransformType, transformOptions } from 'src/utils'

type InfiniteSelectPopupRender = ReactNode | SelectProps['popupRender']

export type InfiniteSelectProps<
  TData extends TDataShape,
  TPaginatedData extends AnyObject,
  TRetrieveData
> = {
  getQueryFunction: (
    options: Options<TData>
  ) => UseInfiniteQueryOptions<
    TPaginatedData,
    AxiosError<Error, unknown>,
    InfiniteData<TPaginatedData>,
    QueryKey<Options<TData>>,
    | number
    | Pick<QueryKey<Options<TData>>[0], 'body' | 'headers' | 'path' | 'query'>
  >
  baseOptions?: Options<TData>
  getOptions?: (params: OptionTransformType<TRetrieveData>) => DefaultOptionType
  isQueryDisabled?: boolean
  popupRender?: InfiniteSelectPopupRender
  filterKey?: string
} & Omit<SelectProps, 'popupRender'>

export const InfiniteSelect = <
  TData extends TDataShape,
  TPaginatedData extends AnyObject,
  TRetrieveData
>({
  getQueryFunction,
  baseOptions,
  getOptions = transformOptions,
  isQueryDisabled,
  popupRender,
  filterKey,
  ...props
}: InfiniteSelectProps<TData, TPaginatedData, TRetrieveData>) => {
  const { t } = useTranslation()
  const filterLabels = useFilterLabels()

  const [search, setSearch] = useState<string>()
  const debouncedSearch = useDebouncedValue(search)

  const mergedOptions = useMemo<Options<TData>>(() => {
    const base = (baseOptions ?? {}) as Options<TData> &
      Pick<TDataShape, 'query'>
    const baseQuery = (base.query ?? {}) as Record<string, unknown>

    const pref = baseQuery.preference

    const getIsValidPreference = (value: unknown) =>
      value !== undefined &&
      value !== null &&
      value !== false &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)

    const normalizePreference = (value: unknown) =>
      typeof value === 'number' ? String(value) : value

    const shouldUsePref = props.labelInValue || getIsValidPreference(pref)

    const rawPreference = shouldUsePref
      ? pref
      : getIsValidPreference(props.value)
        ? props.value
        : undefined
    const preference = normalizePreference(rawPreference)

    return {
      ...base,
      query: {
        ...baseQuery,
        preference,
        search: debouncedSearch
      }
    }
  }, [baseOptions, debouncedSearch, props.labelInValue, props.value])

  const {
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: isDataLoading,
    hasNextPage
  } = useInfiniteQuery({
    ...getQueryFunction(mergedOptions),
    getNextPageParam: (lastPage) => lastPage?.next,
    initialPageParam: 1,
    placeholderData: (previousData) => previousData,
    enabled: !props.disabled || !isQueryDisabled
  })

  const options = useMemo(() => {
    return data?.pages?.flatMap((page) =>
      getOptions({ options: page?.results ?? [] })
    )
  }, [data, getOptions])

  useEffect(() => {
    if (filterKey) filterLabels?.registerOptions(filterKey, options)
  }, [filterKey, filterLabels, options])

  const value = useMemo(() => {
    const normalizeValue = (value: unknown) => {
      if (typeof value !== 'number') return value

      const stringValue = String(value)

      return options?.some((option) => option?.value === stringValue)
        ? stringValue
        : value
    }

    return Array.isArray(props.value)
      ? props.value.map(normalizeValue)
      : normalizeValue(props.value)
  }, [options, props.value])

  const onPopupScroll: SelectProps['onPopupScroll'] = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const target = e?.target as HTMLElement

      if (
        hasNextPage &&
        !isFetching &&
        target.scrollHeight - target.scrollTop <= target.clientHeight + 1
      ) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetching]
  )

  const isSearchPending = search !== debouncedSearch
  const isLoading =
    isDataLoading || isSearchPending || (isFetching && !isFetchingNextPage)

  const notFoundContent = useMemo(
    () =>
      isLoading ? (
        <Flex justify="center">
          <Spin />
        </Flex>
      ) : (
        <Empty />
      ),
    [isLoading]
  )

  const showSearch = useMemo(
    () => ({
      filterOption: false,
      searchValue: search,
      onSearch: setSearch
    }),
    [search]
  )

  const resolvedPopupRender: SelectProps['popupRender'] = useCallback(
    (menu: ReactElement) => {
      if (!popupRender) {
        return menu
      }

      if (typeof popupRender === 'function') {
        return popupRender(menu)
      }

      return (
        <>
          {menu}

          <Divider className="mt-12 mb-8" />
          {popupRender}
        </>
      )
    },
    [popupRender]
  )

  return (
    <Select
      options={options}
      loading={isLoading}
      allowClear
      popupRender={resolvedPopupRender}
      showSearch={showSearch}
      maxTagCount="responsive"
      popupMatchSelectWidth={false}
      placeholder={t('select')}
      onPopupScroll={onPopupScroll}
      {...props}
      notFoundContent={notFoundContent}
      value={value}
    />
  )
}
